import {
  type RouteLocation,
  type RouteLocationNormalized,
  type RouteLocationNormalizedLoaded,
  createRouter,
  createWebHistory,
} from 'vue-router'

import cookiesGuard from './guards/cookies'
import fetchFeatureFlags from './guards/fetch-feature-flags'
import fetchFeaturedCampaign from './guards/fetch-featured-campaign'
import { fetchInstantWins } from './guards/fetch-instant-wins'
import fetchNextPrize from './guards/fetch-next-prize'
import { fetchPlans } from './guards/fetch-plans'
// import { fetchUnplayedSessionsGuard } from './guards/fetch-unplayed-sessions'
import fetchUserDetailsGuard from './guards/fetch-user'
import magicLinkAuthGuard from './guards/magic-link-auth'
import { endPageProgress, startPageProgress } from './guards/page-progress'
import setPageTitle from './guards/page-title'
import showNotification from './guards/show-notification'
import { setPreviousRoute } from './historyTracker'
import { handleUrlParameters } from './middleware/url-params'
import routes from './routes'

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL ?? '/'),

  // https://router.vuejs.org/guide/advanced/scroll-behavior
  scrollBehavior(
    to: RouteLocationNormalized,
    from: RouteLocationNormalizedLoaded
  ) {
    // always scroll to top
    return new Promise((resolve) => {
      setTimeout(
        () => {
          if (to.hash) {
            resolve({ el: to.hash, top: 70, behavior: 'smooth' })
          } else {
            resolve({ top: 0, behavior: 'instant' })
          }
        },
        to.path === from.path ? 0 : to.hash ? 1000 : 250
      )
    })
  },
  linkActiveClass: 'active-link',
  routes,
})

// https://inside.365talents.com/blog/preload-pages-vue-router

router.beforeResolve((to: RouteLocation, from: RouteLocation) => {
  if (to.name !== from.name) {
    startPageProgress()
  }
  return true
})

router.beforeEach(async (to: RouteLocation, from: RouteLocation) => {
  const subdomain = window.location.hostname.split('.')[0]
  if (subdomain === 'r' || subdomain === 'share') {
    const mainDomain = window.location.hostname.replace(/^(r|share)\./, '')
    const path = `/${subdomain}${window.location.pathname}`
    window.location.replace(`${window.location.protocol}//${mainDomain}${path}`)
    return false
  }

  if (to.name === from.name) {
    setPreviousRoute(from)
    return true
  }

  try {
    cookiesGuard()

    const magicLinkResult = await magicLinkAuthGuard(to)
    if (magicLinkResult !== true) {
      return magicLinkResult
    }

    await Promise.all([
      fetchUserDetailsGuard(to),
      fetchFeaturedCampaign(to),
      fetchNextPrize(to),
      fetchPlans(),
      fetchFeatureFlags(),
      // fetchUnplayedSessionsGuard(to, router),
    ])

    // Fetch instant wins in the background for routes that need them
    // This is non-blocking and won't prevent navigation
    fetchInstantWins(to).catch((error) => {
      console.error('Background instant wins fetch failed:', error)
    })

    setPreviousRoute(from)

    return true
  } catch (error) {
    console.error('Navigation guard error:', error)
    return false
  }
})

router.afterEach(async (to: RouteLocation, from: RouteLocation) => {
  if (to.name === from.name) {
    return
  }

  setPageTitle(to, from)
  endPageProgress()
  showNotification(to)

  // Navigation handled by middleware (e.g. URL param processing)
  await handleUrlParameters(to, router)
})

/**
 * Handles errors that occur during routing and navigation
 * when we deploy new versions of the app.
 */
router.onError((error: Error, to: RouteLocationNormalized | undefined) => {
  console.error('Router error:', error)

  const msg = (error?.message || '').toLowerCase()
  const patterns = [
    'loading chunk',
    'failed to load module script',
    'not a valid javascript mime type',
    'mime type of "text/html"',
    'failed to fetch dynamically imported module',
    'error loading dynamically imported module',
  ]

  const isChunkErr = patterns.some((p) => msg.includes(p))

  if (!isChunkErr || !to?.fullPath) {
    return
  }

  const key = `route-retry:${to.fullPath}`

  try {
    const hasRetried = sessionStorage.getItem(key)

    if (!hasRetried) {
      sessionStorage.setItem(key, '1')
      const sep = to.fullPath.includes('?') ? '&' : '?'
      window.location.replace(`${to.fullPath}${sep}v=${Date.now()}`)
    } else {
      sessionStorage.removeItem(key)
      if (router.hasRoute('not-found')) {
        void router.replace({ name: 'not-found' })
      } else {
        void router.replace('/')
      }
    }
  } catch (storageError) {
    console.warn('Storage operation failed:', storageError)
    const sep = to.fullPath.includes('?') ? '&' : '?'
    window.location.replace(`${to.fullPath}${sep}v=${Date.now()}`)
  }
})

if (import.meta.env.GLP_APP_ENV !== 'production') {
  router.addRoute({
    path: '/catalogue-debug',
    name: 'catalogue-debug',
    component: () => import('../views/CatalogueDebugView.vue'),
  })
}

export default router
